import React, { useEffect, useState } from "react";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  FormControlLabel,
  Grid,
  IconButton,
  Switch,
  Typography,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ZoomInIcon from "@mui/icons-material/ZoomIn";
import ClearIcon from "@mui/icons-material/Clear";
import DoneIcon from "@mui/icons-material/Done";
import OpenInBrowserIcon from "@mui/icons-material/OpenInBrowser";
import smpData from "./config.json";
import { initiateAXIOS } from "../../../../../config/axios";
import { initiateScheduleAPI } from "../../../../../api";
import { useTranslation } from "react-i18next";
import { parseVariables } from "../../../../../helpers/localize";
import { showSanitizedInput } from "../../../../../helpers/inputProcessing";
import parser from "html-react-parser";
import { logger } from "../../../../../helpers/logger";
import { useNavigate } from "react-router-dom";

const ImageWithFallback = ({ src, alt, style }) => {
  const { t } = useTranslation("common");
  const [show, setShow] = useState(true);
  const [open, setOpen] = useState(false);
  const [hover, setHover] = useState(false);

  if (!show) return null;

  return (
    <>
      <Box
        position="relative"
        onMouseEnter={() => setHover(true)}
        onMouseLeave={() => setHover(false)}
        onClick={() => setOpen(true)}
        sx={{ cursor: "pointer", display: "inline-block" }}
      >
        <img src={src} alt={alt} style={style} onError={() => setShow(false)} />
        {hover && (
          <IconButton
            size="small"
            sx={{
              position: "absolute",
              top: 4,
              right: 4,
              backgroundColor: "rgba(0,0,0,0.5)",
              color: "white",
              "&:hover": { backgroundColor: "rgba(0,0,0,0.7)" },
            }}
          >
            <ZoomInIcon fontSize="small" />
          </IconButton>
        )}
      </Box>

      <Dialog open={open} onClose={() => setOpen(false)} maxWidth="md">
        <DialogContent sx={{ p: 0 }}>
          <img src={src} alt={alt} style={{ width: "100%", height: "auto" }} />
        </DialogContent>
        <DialogActions sx={{ display: "flex", justifyContent: "center" }}>
          <Button variant="outlined" onClick={() => setOpen(false)}>
            {t("closeBtn")}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

const AnnouncementToggle = ({ announcementId, title, isOpen }) => {
  const { t } = useTranslation("announcement");
  const { t: tCommon } = useTranslation("common");

  const [loading, setLoading] = useState(false);
  const [disabled, setDisabled] = useState(true);
  const [announcementData, setAnnouncementData] = useState({});
  const [openConfirmation, setOpenConfirmation] = useState(false);

  useEffect(() => {
    setLoading(false);
    setDisabled(true);

    if (announcementId && isOpen) getAnnouncement(announcementId);

    // eslint-disable-next-line
  }, [announcementId, isOpen]);

  const getAnnouncement = async (id) => {
    const access_token = sessionStorage.getItem("access_token");
    try {
      setLoading(true);
      const { data } = await initiateAXIOS.get(initiateScheduleAPI + id, {
        headers: { authorization: `Bearer ${access_token}` },
      });
      const annData = data;
      setAnnouncementData(annData);
      setDisabled(false);
    } catch (e) {
      logger.error(e);
      setDisabled(true);
    } finally {
      setLoading(false);
    }
  };

  const toggleAnnouncement = async (id, checked) => {
    const access_token = sessionStorage.getItem("access_token");
    try {
      setDisabled(true);
      setLoading(true);
      await initiateAXIOS.post(
        initiateScheduleAPI + id + (checked ? "/activate" : "/deactivate"),
        {},
        {
          headers: { authorization: `Bearer ${access_token}` },
        }
      );

      setAnnouncementData({ ...announcementData, isActive: checked });
    } catch (e) {
      logger.error(e);
    } finally {
      setOpenConfirmation(false);
      setDisabled(false);
      setLoading(false);
    }
  };

  return (
    <>
      <FormControlLabel
        sx={{ mr: 0 }}
        control={
          loading ? (
            <CircularProgress sx={{ mx: 2 }} size={24} />
          ) : (
            <Switch
              disabled={disabled || !announcementData?.isApproved}
              checked={announcementData?.isActive}
              onClick={() => setOpenConfirmation(true)}
            />
          )
        }
        label={title}
      />
      <Dialog
        fullWidth
        maxWidth="sm"
        open={openConfirmation}
        onClose={() => {
          setOpenConfirmation(false);
        }}
      >
        <DialogContent>
          <DialogContentText
            sx={{
              textAlign: "center",
              mb: 2,
            }}
          >
            {announcementData?.isActive
              ? parser(
                  parseVariables(t("item.turnOFFConfirmationText"), {
                    name: showSanitizedInput(announcementData?.name || ""),
                  })
                )
              : parser(
                  parseVariables(t("item.turnONConfirmationText"), {
                    name: showSanitizedInput(announcementData?.name || ""),
                  })
                )}
          </DialogContentText>
        </DialogContent>

        <DialogActions>
          <Button
            color="error"
            variant="outlined"
            fullWidth
            startIcon={<ClearIcon />}
            onClick={() => setOpenConfirmation(false)}
            disabled={loading}
          >
            {tCommon("cancelBtn")}
          </Button>

          <Button
            color={announcementData?.isActive ? "warning" : "primary"}
            variant="contained"
            fullWidth
            startIcon={loading ? null : <DoneIcon />}
            onClick={() =>
              toggleAnnouncement(
                announcementId,
                announcementData?.isActive ? false : true
              )
            }
            disabled={loading}
          >
            {loading ? (
              <CircularProgress sx={{ mx: 2 }} size={24} />
            ) : announcementData?.isActive ? (
              t("item.turnOFFBtn")
            ) : (
              t("item.turnONBtn")
            )}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

const NewSmpButton = () => {
  const { t } = useTranslation("bplrtSmp");
  const { t: tCommon } = useTranslation("common");
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [expanded, setExpanded] = useState(false);

  const handleChange = (panel) => (event, isExpanded) => {
    setExpanded(isExpanded ? panel : false);
  };

  const handleClose = () => {
    setOpen(false);
  };

  return (
    <>
      <Button
        variant="contained"
        onClick={() => {
          setOpen(true);
        }}
        fullWidth
        sx={{ boxShadow: 3 }}
      >
        {t("newSmp.title")}
      </Button>

      <Dialog open={open} maxWidth="md" fullWidth onClose={handleClose}>
        <DialogTitle
          sx={{
            display: "flex",
            justifyContent: "space-between",
          }}
        >
          {t("newSmp.title")}
        </DialogTitle>
        <DialogContent>
          {Object.entries(smpData).map(([classKey, classValue]) => (
            <Accordion
              key={classKey}
              expanded={expanded === classKey}
              onChange={handleChange(classKey)}
            >
              <AccordionSummary
                expandIcon={<ExpandMoreIcon />}
                aria-controls="panel1-content"
                id="panel1-header"
              >
                <Typography component="span">{classValue.title}</Typography>
              </AccordionSummary>
              <AccordionDetails>
                <Grid container spacing={2}>
                  {Object.entries(classValue.type).map(
                    ([typeKey, typeValue]) => (
                      <Grid item xs={12} md={12} key={typeKey}>
                        <Box display="flex" alignItems="center" gap={2}>
                          <ImageWithFallback
                            src={`/assets/newSmpImage/${
                              classKey + "/" + typeKey + ".png"
                            }`}
                            alt={
                              classValue.title +
                              " " +
                              typeValue.title +
                              " Image"
                            }
                            style={{ width: 60, height: 60, borderRadius: 8 }}
                          />
                          <AnnouncementToggle
                            title={typeValue.title}
                            announcementId={typeValue.announcementId}
                            isOpen={expanded === classKey}
                          />
                          <IconButton
                            sx={{
                              width: 34,
                              height: 34,
                              color: "default",
                              "&:hover": {
                                color: "primary.main",
                                backgroundColor: "rgba(28, 78, 216, 0.1)",
                              },
                            }}
                            onClick={() =>
                              navigate(
                                "/announcement/detail/" +
                                  typeValue.announcementId
                              )
                            }
                          >
                            <OpenInBrowserIcon sx={{ fontSize: 22 }} />
                          </IconButton>
                        </Box>
                      </Grid>
                    )
                  )}
                </Grid>
              </AccordionDetails>
            </Accordion>
          ))}
        </DialogContent>
        <DialogActions sx={{ display: "flex", justifyContent: "center" }}>
          <Button variant="outlined" onClick={handleClose}>
            {tCommon("closeBtn")}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default NewSmpButton;
